/**
 * #120936 — Nền chung cho "vào thẳng màn hình bằng tham số URL" (deep-link).
 *
 * Quy ước tên tham số (chốt ở KHUON-CHUAN-DEEPLINK.md mục 2, dev-advance #120015):
 * snake_case, tiếng Anh, SỐ ÍT, TRÙNG ĐÚNG tên tham số API.
 * Nhiều giá trị → CSV (`status=ACTIVE,INACTIVE`), không lặp key, không số nhiều.
 *
 * File này CỐ Ý không phụ thuộc react-router: `BranchContext` nằm NGOÀI `RouterProvider`
 * (`src/index.js`) nên không dùng được `useSearchParams` — xem `src/utils/routerBridge.js`.
 */

/** Chuỗi query hiện tại của trình duyệt (luôn là nguồn tươi nhất). */
export const currentSearch = () => (typeof window === 'undefined' ? '' : window.location.search);

/** `URLSearchParams` đọc từ URL thật đang hiển thị. */
export const currentParams = () => new URLSearchParams(currentSearch());

/**
 * Đọc 1 tham số, chấp nhận cả tên cũ lệch chuẩn làm alias (vd `branchId` cho `branch_id`).
 * Chỉ ĐỌC alias — khi ghi ra luôn dùng tên chuẩn (mục 2 của khuôn).
 */
export const getParam = (params, name, aliases = []) => {
  for (const key of [name, ...aliases]) {
    const raw = params.get(key);
    if (raw !== null && raw !== '') return raw;
  }
  return null;
};

/** Giá trị -> chuỗi để đặt lên URL. Mảng thành CSV. */
export const serializeValue = (value, serialize) => {
  if (typeof serialize === 'function') return serialize(value);
  if (Array.isArray(value)) return value.join(',');
  return value === null || value === undefined ? '' : String(value);
};

/**
 * Đọc một bộ tham số theo `spec`, áp đúng 3 luật cứng ở mục 3 của khuôn:
 * tham số lạ thì BỎ QUA về mặc định CỦA RIÊNG nó (không reset param khác, không throw,
 * không trắng màn) và phải báo lại được để hiện băng cảnh báo NHÌN THẤY ĐƯỢC.
 *
 * @returns {{ values: Object, ignored: Array<{name: string, raw: string, fallback: any}> }}
 */
export const readValues = (params, spec) => {
  const values = {};
  const ignored = [];

  Object.entries(spec).forEach(([name, cfg]) => {
    const fallback = typeof cfg.default === 'function' ? cfg.default() : cfg.default;
    const raw = getParam(params, name, cfg.alias);

    if (raw === null) {
      values[name] = fallback;
      return;
    }

    if (typeof cfg.valid === 'function' && !cfg.valid(raw)) {
      values[name] = fallback;
      ignored.push({ name, raw, fallback });
      return;
    }

    try {
      values[name] = typeof cfg.parse === 'function' ? cfg.parse(raw) : raw;
    } catch (err) {
      values[name] = fallback;
      ignored.push({ name, raw, fallback });
    }
  });

  return { values, ignored };
};

/**
 * Dựng `URLSearchParams` mới từ `base`, ghi ĐÈ đúng các param thuộc `spec`.
 *
 * Hai luật của khuôn được thực thi ở đây:
 * - **Chỉ ghi param KHÁC mặc định** (mục 1c-3) — bằng mặc định thì xoá hẳn khỏi URL.
 * - Tên cũ lệch chuẩn chỉ được ĐỌC, không bao giờ được giữ lại khi ghi → xoá alias.
 *
 * Param NGOÀI `spec` (vd `branch_id` do BranchContext quản) được giữ nguyên.
 */
export const writeValues = (base, spec, values) => {
  const next = new URLSearchParams(base.toString());

  Object.entries(spec).forEach(([name, cfg]) => {
    (cfg.alias || []).forEach((alias) => next.delete(alias));

    const fallback = typeof cfg.default === 'function' ? cfg.default() : cfg.default;
    const value = values[name];
    const asText = serializeValue(value, cfg.serialize);

    if (asText === '' || asText === serializeValue(fallback, cfg.serialize)) {
      next.delete(name);
    } else {
      next.set(name, asText);
    }
  });

  return next;
};

/** `?a=1&b=2` (hoặc chuỗi rỗng) — dùng trực tiếp cho `replace`. */
export const toSearchString = (params) => {
  const text = params.toString();
  return text ? `?${text}` : '';
};
